import React, { useState } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import CustomSelect from '../../../components/ui/CustomSelect';
import { parseRupiah } from '../../hris/recruitment/utils/formatRupiah';

interface Step5Props {
  formData: any;
  handleChange: (field: string, value: any) => void;
}

export default function Step5SLIK({ formData, handleChange }: Step5Props) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const formatCurrencyInput = (value: any): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  const handleCurrencyChange = (field: string, value: string) => {
    const rawValue = parseRupiah(value);
    if (rawValue === '') {
      handleChange(field, '');
      return;
    }
    const numValue = parseFloat(rawValue);
    handleChange(field, isNaN(numValue) ? '' : rawValue);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      handleChange('slik_document_file', file);
    }
  };

  const removeFile = () => {
    setIsRemoving(true);
    setTimeout(() => {
      setFileName(null);
      setIsRemoving(false);
      handleChange('slik_document_file', null);
      handleChange('slik_document_url', '');
    }, 300);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Dokumen SLIK</label>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          id="slik-upload"
          onChange={handleFileChange}
        />
        {fileName ? (
          <div
            className={`flex items-center justify-between p-3 border-2 border-blue-200 bg-blue-50 rounded-lg ${isRemoving ? 'animate-imageOut' : 'animate-imageIn'}`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">{fileName}</span>
            </div>
            <button
              type="button"
              onClick={removeFile}
              className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 hover:scale-110 transition-all duration-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label
            htmlFor="slik-upload"
            className="flex flex-col items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 group"
          >
            <Upload className="w-8 h-8 text-gray-400 group-hover:text-blue-500 group-hover:scale-110 transition-all duration-300" />
            <span className="text-sm text-gray-500 group-hover:text-blue-600 transition-colors duration-300">
              Pilih dokumen SLIK (.pdf, .jpg, .png)
            </span>
          </label>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Plafon Efektif (Rp)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
            <input
              type="text"
              value={formatCurrencyInput(formData.slik_effective_plafon)}
              onChange={e => handleCurrencyChange('slik_effective_plafon', e.target.value)}
              placeholder="0"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Baki Debet (Rp)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
            <input
              type="text"
              value={formatCurrencyInput(formData.slik_outstanding)}
              onChange={e => handleCurrencyChange('slik_outstanding', e.target.value)}
              placeholder="0"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kualitas Terburuk</label>
          <CustomSelect
            value={formData.slik_worst_quality}
            onChange={v => handleChange('slik_worst_quality', v)}
            placeholder="Pilih"
            width="w-full"
            color="blue"
            options={[
              { value: '1', label: 'Kolektibilitas 1 - Lancar' },
              { value: '2', label: 'Kolektibilitas 2 - DPK' },
              { value: '3', label: 'Kolektibilitas 3 - Kurang Lancar' },
              { value: '4', label: 'Kolektibilitas 4 - Diragukan' },
              { value: '5', label: 'Kolektibilitas 5 - Macet' },
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bulan Data</label>
          <input
            type="text"
            value={formData.slik_data_month}
            onChange={e => handleChange('slik_data_month', e.target.value)}
            placeholder="Contoh: Januari 2024"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Posisi SLIK
          </label>
          <input
            type="date"
            value={formData.slik_position_date}
            onChange={e => handleChange('slik_position_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>
      </div>
    </div>
  );
}
